<template>
  <div class="mt-8 flex flex-col gap-y-6">
    <!-- Quarterly Comparison Cards -->
    <div v-if="loadedData" class="w-full flex flex-col gap-y-8">
      <h2 class="text-2xl font-bold text-gray-800">Análisis Trimestral</h2>

      <!-- Unified Legend -->
      <RejectionLegend />

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          v-for="(quarter, index) in this.quarterlyData?.quarters ?? []"
          :key="index"
          :class="[
            'relative rounded-xl overflow-hidden shadow-lg transition-all duration-300 bg-white',
            {
              'hover:shadow-xl transform hover:scale-105':
                quarter.metrics.total_inspections > 0
            }
          ]"
        >
          <!-- Header con color de trimestre -->
          <div
            :class="[
              'h-3',
              quarter.metrics.total_inspections > 0
                ? getRejectionBarClass(quarter.metrics.avg_rejection_percentage)
                : 'bg-gray-300'
            ]"
          ></div>

          <div class="p-6">
            <h4 class="font-semibold mb-4 text-lg text-gray-800">
              {{ quarter.quarter_label }}
            </h4>

            <!-- Sin datos -->
            <div
              v-if="quarter.metrics.total_inspections === 0"
              class="text-center py-4"
            >
              <p class="text-gray-400 text-sm">
                No hay registros de inspección
              </p>
            </div>

            <!-- Mini gráfico - Barras de rechazo por proveedor -->
            <div v-else-if="quarter.providers.length > 0" class="mb-6">
              <p class="text-xs text-gray-500 mb-2">Distribución de rechazo</p>
              <div class="flex items-end gap-1 h-12">
                <div
                  v-for="(provider, idx) in quarter.providers"
                  :key="`${index}-${idx}`"
                  class="flex-1 flex flex-col items-center group relative"
                >
                  <div
                    :class="getRejectionBarClass(provider.rejection_percentage)"
                    class="w-full rounded-t transition-all duration-200 cursor-pointer h-4"
                  ></div>
                  <!-- Tooltip al hover -->
                  <div
                    class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none"
                  >
                    {{ provider.name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats principales -->
            <div
              v-if="quarter.metrics.total_inspections > 0"
              class="space-y-3 mb-4 pb-4 border-b border-gray-200"
            >
              <div class="flex justify-between items-center">
                <span class="text-gray-600 text-sm">Inspecciones</span>
                <span class="font-semibold text-gray-800">
                  {{ getFormatNumber(quarter.metrics.total_inspections || 0) }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600 text-sm">% Rechazo Promedio</span>
                <span
                  :class="[
                    'font-semibold text-lg',
                    getRejectionClass(quarter.metrics.avg_rejection_percentage)
                  ]"
                >
                  {{
                    (quarter.metrics.avg_rejection_percentage || 0).toFixed(2)
                  }}%
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600 text-sm">Cajas Rechazadas</span>
                <span class="font-semibold text-gray-800">
                  {{ getFormatNumber(calculateTotalRejected(quarter)) }}
                </span>
              </div>
            </div>

            <!-- Badge de estado -->
            <div v-if="quarter.metrics.total_inspections > 0">
              <span
                :class="
                  getStatusBadgeClass(quarter.metrics.avg_rejection_percentage)
                "
              >
                {{ getStatusLabel(quarter.metrics.avg_rejection_percentage) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend Chart: Line Chart by Providers -->
      <LineChart
        title="Tendencia de Tasa de Rechazo por Trimestre"
        subtitle="Últimos 4 trimestres"
        description="Muestra cómo ha evolucionado el porcentaje de rechazo de cada proveedor a lo largo de los trimestres."
        :categories="quarterLabels"
        :series="providerTrendSeries"
        :tooltip-formatter="val => val.toFixed(2) + '%'"
        y-title="Tasa de Rechazo (%)"
        :colors="[]"
        display-data-labels
        stroke-curve="smooth"
      ></LineChart>

      <!-- Comparison Table -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">
          Matriz de Comparación Trimestral
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b">
              <tr>
                <th class="text-left px-4 py-2 font-semibold">Proveedor</th>
                <th
                  v-for="quarter in quarterlyData.quarters"
                  :key="quarter.quarter_id"
                  class="text-center px-4 py-2 font-semibold"
                >
                  {{ quarter.quarter_label }}
                </th>
                <th class="text-center px-4 py-2 font-semibold">Promedio</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="provider in allProviders"
                :key="provider.id"
                class="border-b hover:bg-slate-50"
              >
                <td class="text-left px-4 py-2 font-medium">
                  {{ provider.name }}
                </td>
                <td
                  v-for="quarter in quarterlyData.quarters"
                  :key="`${provider.id}-${quarter.quarter_id}`"
                  class="text-center px-4 py-2 rounded"
                  :class="
                    isProviderHasInspectionsInQuarter(
                      provider.id,
                      quarter.quarter_id
                    )
                      ? getHeatMapClasses(
                          getProviderQuarterlyRejection(
                            provider.id,
                            quarter.quarter_id
                          )
                        )
                      : ''
                  "
                >
                  {{
                    isProviderHasInspectionsInQuarter(
                      provider.id,
                      quarter.quarter_id
                    )
                      ? getProviderQuarterlyRejection(
                          provider.id,
                          quarter.quarter_id
                        ).toFixed(2) + "%"
                      : "-"
                  }}
                </td>
                <td class="text-center px-4 py-2 font-medium">
                  {{ calculateProviderAverage(provider.id).toFixed(2) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/quality_reports/process/show_process.js";
import LineChart from "@/components/views/layout/quality_reports/LineChart.vue";
import RejectionLegend from "@/components/views/layout/quality_reports/RejectionLegend.vue";
import {
  getRejectionBarClass,
  getRejectionClass,
  getHeatMapClasses,
  getStatusBadgeClass,
  getStatusLabel
} from "@/components/views/mixins/agro_exporter/quality_reports/rejection_classification.js";

export default {
  name: "QualityReportsContainerProviderShowProcessQuarterlyAnalysis",
  components: {
    LineChart,
    RejectionLegend
  },
  props: {
    agro_exporter_id: { required: true },
    agro_exporter_process_id: { required: true }
  },
  data() {
    return {
      loadedData: false,
      quarterlyData: null,
      colors: [
        "#ef4444",
        "#f59e0b",
        "#84cc16",
        "#22d3ee",
        "#1e40af",
        "#6d28d9",
        "#e879f9",
        "#334155",
        "#10b981",
        "#ec4899",
        "#0ea5e9",
        "#a855f7",
        "#f97316",
        "#14b8a6",
        "#facc15"
      ]
    };
  },
  computed: {
    latestQuarter() {
      return (
        this.quarterlyData?.quarters[this.quarterlyData.quarters.length - 1] ||
        {}
      );
    },
    quarterLabels() {
      return this.quarterlyData?.quarters.map(q => q.quarter_label) || [];
    },
    allProviders() {
      const providersMap = new Map();
      this.quarterlyData?.quarters.forEach(quarter => {
        quarter.providers.forEach(provider => {
          if (!providersMap.has(provider.id)) {
            providersMap.set(provider.id, {
              id: provider.id,
              name: provider.name,
              color: provider.color
            });
          }
        });
      });
      return Array.from(providersMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
    providerTrendSeries() {
      return this.allProviders.map(provider => {
        const data = this.quarterlyData.quarters.map(quarter => {
          const providerData = quarter.providers.find(
            p => p.id === provider.id
          );
          return {
            x: quarter.quarter_label,
            y: providerData ? parseFloat(providerData.rejection_percentage) : 0
          };
        });
        return {
          name: provider.name,
          color: provider.color,
          data: data
        };
      });
    },
  },
  methods: {
    async fetchQuarterlyReport() {
      try {
        const { data } = await Requester.fetchQuarterlyReport(
          this.agro_exporter_id,
          this.agro_exporter_process_id
        );
        this.quarterlyData = data;
        this.loadedData = true;
      } catch (error) {
        console.error("Error fetching quarterly analysis:", error);
        this.loadedData = true;
      }
    },
    calculateTotalRejected(quarter) {
      return (
        quarter.providers?.reduce((sum, p) => sum + p.total_rejected, 0) || 0
      );
    },
    getProviderQuarterlyRejection(providerId, quarterId) {
      const quarter = this.quarterlyData.quarters.find(
        q => q.quarter_id === quarterId
      );
      if (!quarter) return 0;
      const provider = quarter.providers.find(p => p.id === providerId);
      return provider ? parseFloat(provider.rejection_percentage) : 0;
    },
    calculateProviderAverage(providerId) {
      const values = this.quarterlyData.quarters
        .filter(q => q.metrics.total_inspections > 0)
        .map(q => this.getProviderQuarterlyRejection(providerId, q.quarter_id));
      const sum = values.reduce((a, b) => a + b, 0);
      return sum / values.length;
    },
    getHeatMapClasses(value) {
      return getHeatMapClasses(value);
    },
    getRejectionBarClass(rejectionPercentage) {
      return getRejectionBarClass(rejectionPercentage);
    },
    getRejectionClass(rejectionPercentage) {
      return getRejectionClass(rejectionPercentage);
    },
    getStatusBadgeClass(rejectionPercentage) {
      return getStatusBadgeClass(rejectionPercentage);
    },
    getStatusLabel(rejectionPercentage) {
      return getStatusLabel(rejectionPercentage);
    },
    getFormatNumber(value) {
      return new Intl.NumberFormat("es-ES").format(value);
    },
    isProviderHasInspectionsInQuarter(providerId, quarterId) {
      const quarter = this.quarterlyData.quarters.find(
        q => q.quarter_id === quarterId
      );
      if (!quarter) return false;
      const provider = quarter.providers.find(p => p.id === providerId);
      return provider && provider.total_inspections > 0;
    }
  },
  created() {
    this.fetchQuarterlyReport();
  }
};
</script>

<style scoped>
table {
  border-collapse: collapse;
}
</style>
